import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { useSearchParams, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { ExperienceCard } from '../components/ExperienceCard';
import { FilterBar } from '../components/FilterBar';
import { SkeletonGrid } from '../components/SkeletonCard';
import { SEOHead } from '../components/SEOHead';
import { buildItemListJsonLd, buildBreadcrumbJsonLd } from '../lib/seoJsonLd';
import { fetchClickStats } from '../lib/popularExperiences';
import { FilterX } from 'lucide-react';
import { BACKEND_URL } from '../lib/apiBase';
import { toUrlCategorySlug, fromUrlCategorySlug } from '../lib/categoryUrlSlug';

export function ExperiencesPage() {
  const { lang, t, localized } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // When mounted at `/experience/:slug`, react-router-dom populates params
  // with the slug. Used to surface a single experience in "You selected"
  // mode while the rest of the grid narrows to its most-populated category.
  const { slug: selectedSlug } = useParams();
  const [experiences, setExperiences] = useState([]);
  const [allExperiences, setAllExperiences] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  // Bumped every time the user presses "Clear all filters". FilterBar
  // listens to this counter and resets its horizontal scroll position
  // back to the start so the freshly-cleared pill row is visible.
  const [clearAllSignal, setClearAllSignal] = useState(0);
  // Click counts keyed by experience id, fetched once on mount. The ranked
  // grid below uses these to surface the most-clicked cards first; until
  // they load we keep the natural backend order so the page paints fast.
  const [clickStats, setClickStats] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchClickStats().then((stats) => { if (alive) setClickStats(stats); });
    return () => { alive = false; };
  }, []);
  // Upper bound of the price filter. We seed it at 1000 so a fresh mount
  // never hides €249+ experiences, then raise it dynamically once the
  // experiences list lands (ceil to the next 50 above the actual max).
  const DEFAULT_MAX_PRICE = 1000;
  const [maxPrice, setMaxPrice] = useState(DEFAULT_MAX_PRICE);
  // Detect a hard reload of the Explore page. When true we ignore any
  // existing URL query params (category / location / search / filter) so
  // the page always opens in a clean state, and we also strip them from
  // the URL via a replace-navigate below. Normal in-app navigation (e.g.
  // clicking a category pill on the homepage) still carries filters as
  // expected because those mounts are not reloads.
  const isReloadRef = useRef(
    typeof window !== 'undefined' &&
    (performance.getEntriesByType('navigation')[0]?.type === 'reload')
  );
  const [filters, setFilters] = useState(() => {
    if (isReloadRef.current) {
      return {
        categories: [],
        locations: [],
        durations: [],
        languages: [],
        priceRange: [0, DEFAULT_MAX_PRICE],
        search: ''
      };
    }
    return {
      categories: searchParams.get('filter') === 'events'
        ? ['events']
        : (searchParams.get('category') ? [fromUrlCategorySlug(searchParams.get('category'))] : []),
      locations: searchParams.get('location') ? [searchParams.get('location')] : [],
      durations: [],
      languages: [],
      priceRange: [0, DEFAULT_MAX_PRICE],
      search: searchParams.get('search') || ''
    };
  });

  // Sentinel passed to every ExperienceCard so it can reset its transient
  // UI state (flipped face, carousel index, open dropdowns) when the user
  // changes filters or the search query. Cards that survive the filter
  // would otherwise keep their flipped state across the change. We
  // intentionally bump it on price-range release too — anything the user
  // perceives as "applying a filter".
  const filtersResetSignal = React.useMemo(
    () => JSON.stringify({
      c: filters.categories,
      l: filters.locations,
      d: filters.durations,
      lg: filters.languages,
      p: filters.priceRange,
      s: filters.search.trim().toLowerCase(),
    }),
    [filters]
  );

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // On a hard reload, strip any filter-bearing query params from the URL
  // so the page opens clean. We use `replace: true` to avoid polluting
  // the history stack. Guarded so it only fires once on mount.
  useEffect(() => {
    if (!isReloadRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const filterKeys = ['category', 'filter', 'location', 'search'];
    const hasAny = filterKeys.some((k) => params.has(k));
    if (hasAny) {
      navigate(window.location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync filters with URL params when they change. Also scrolls back to the
  // top of the page whenever the search or category param changes from
  // outside the FilterBar (e.g. typing in the desktop header search bar,
  // which updates the URL directly via `navigate()`).
  const urlSyncFirstRun = useRef(true);
  const lastAppliedLocation = useRef(
    isReloadRef.current ? '' : (searchParams.get('location') || '')
  );
  // Track the URL params we last seeded into `filters` so we only re-apply
  // them when the URL actually changes. This is what lets `?filter=events`
  // act as a one-shot homepage hint that survives React StrictMode's double
  // effect invocation in dev (the second invocation sees identical params
  // and short-circuits) and doesn't re-activate the Events pill after the
  // user clicks a different category.
  const lastAppliedFilter = useRef(null);
  const lastAppliedCategory = useRef(null);
  const lastAppliedSearch = useRef(
    isReloadRef.current ? '' : (searchParams.get('search') || '')
  );
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const filterParam = searchParams.get('filter');
    const locationParam = searchParams.get('location') || '';
    const searchParam = searchParams.get('search');

    // On a hard reload, skip the very first URL→filters sync. The reset
    // effect above is about to strip the query params and this effect
    // will fire again with clean values, at which point we let it run
    // normally. Advancing the last-applied refs to match the stale URL
    // ensures that when the params are cleared, the diff is treated as a
    // genuine navigation (null → null) and doesn't falsely trigger
    // scroll-to-top or category re-seeding.
    if (urlSyncFirstRun.current && isReloadRef.current) {
      urlSyncFirstRun.current = false;
      lastAppliedFilter.current = filterParam;
      lastAppliedCategory.current = categoryParam;
      lastAppliedSearch.current = searchParam || '';
      return;
    }

    const categoryUrlChanged =
      filterParam !== lastAppliedFilter.current ||
      categoryParam !== lastAppliedCategory.current;
    const searchUrlChanged = (searchParam || '') !== lastAppliedSearch.current;
    const locationUrlChanged = locationParam !== lastAppliedLocation.current;

    setFilters(prev => {
      const locationChanged = locationParam && locationParam !== lastAppliedLocation.current;
      const seedFromUrl = urlSyncFirstRun.current || locationChanged;
      if (locationParam) lastAppliedLocation.current = locationParam;

      let nextCategories = prev.categories;
      if (categoryUrlChanged) {
        if (filterParam === 'events') {
          nextCategories = ['events'];
        } else {
          nextCategories = categoryParam ? [fromUrlCategorySlug(categoryParam)] : [];
        }
        lastAppliedFilter.current = filterParam;
        lastAppliedCategory.current = categoryParam;
      }
      return {
        ...prev,
        categories: nextCategories,
        locations: seedFromUrl && locationParam ? [locationParam] : prev.locations,
        search: searchParam || ''
      };
    });

    // Track the search param we just applied so subsequent diffs are
    // accurate. We do this outside the setFilters callback so the ref
    // update isn't tied to React's render cycle.
    lastAppliedSearch.current = searchParam || '';

    if (urlSyncFirstRun.current) {
      urlSyncFirstRun.current = false;
      return;
    }
    // Any meaningful URL-driven filter change (category, search, or
    // location) should snap the viewport back to the top of the results
    // so the user immediately sees the new set instead of staying mid-
    // list. This matches what `handleFilterChange` does for in-component
    // filter changes; putting it here also covers external callers like
    // the desktop header search bar and the X clear button, which only
    // update the URL.
    if (categoryUrlChanged || searchUrlChanged || locationUrlChanged) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [searchParams]);

  // Fetch categories from backend
  useEffect(() => {
    async function fetchCategories() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/categories`);
        const data = await res.json();
        // Transform categories to match FilterBar format
        const transformedCategories = (data.categories || []).map(cat => ({
          value: cat.slug,
          label: cat.name_en
        }));
        setCategories(transformedCategories);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    }
    fetchCategories();
  }, []);

  // Fetch all experiences. Extracted so pull-to-refresh can re-trigger it.
  const fetchAllExperiences = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/experiences?lang=${encodeURIComponent(lang)}`);
      const data = await res.json();
      const rawExps = data.experiences || [];

      // Pre-compute a lowercase search haystack per card so the per-keystroke
      // filter loop is one `includes()` per card instead of 4 separate
      // `.toLowerCase()` calls + concats. Re-runs on every fetch, including
      // language switches, so the active-language title/description always
      // contribute to search.
      const exps = rawExps.map((exp) => {
        const titleEn = (exp.title_en || '').toLowerCase();
        const titleLang = (exp[`title_${lang}`] || '').toLowerCase();
        const descEn = (exp.description_en || '').toLowerCase();
        const descLang = (exp[`description_${lang}`] || '').toLowerCase();
        const dest = (exp.destination || '').toLowerCase();
        const cats = (exp.categories || []).join(' ').toLowerCase();
        return {
          ...exp,
          _searchHaystack: `${titleEn} ${titleLang} ${descEn} ${descLang} ${dest} ${cats}`,
        };
      });

      setAllExperiences(exps);
      setExperiences(exps);

      // Dynamically raise the price filter ceiling to cover any outliers.
      // Ceil to the next 50 so the slider handle has some headroom.
      const highest = exps.reduce((m, e) => Math.max(m, Number(e.price) || 0), 0);
      const ceiling = Math.max(DEFAULT_MAX_PRICE, Math.ceil(highest / 50) * 50);
      setMaxPrice(ceiling);
      setFilters((prev) =>
        prev.priceRange[1] === DEFAULT_MAX_PRICE
          ? { ...prev, priceRange: [prev.priceRange[0], ceiling] }
          : prev
      );

      // Extract unique languages from all experiences
      const langSet = new Set();
      exps.forEach(exp => {
        (exp.live_guide_languages || []).forEach(lang => langSet.add(lang));
        (exp.audio_languages || []).forEach(lang => langSet.add(lang));
      });
      setAvailableLanguages(Array.from(langSet).sort());
    } catch (err) {
      console.error('Failed to fetch:', err);
    }
  };

  // Refetch whenever the active language changes. The `/api/experiences`
  // endpoint projects per-language fields server-side (returning only the
  // requested lang + EN fallback to keep payload ~15 KB), so a language
  // switch without a refetch would leave card titles/descriptions stuck on
  // the old language's fields — only UI chrome (which goes through `t()`)
  // would update.
  useEffect(() => {
    (async () => {
      await fetchAllExperiences();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // Pull-to-refresh (mobile only). Detects a downward swipe that starts at
  // the top of the page; the user has to pull past 80px to trigger a refetch.
  const [ptrPull, setPtrPull] = useState(0);
  const [ptrRefreshing, setPtrRefreshing] = useState(false);
  useEffect(() => {
    let startY = null;
    let active = false;
    const PULL_THRESHOLD = 80;
    const MAX_PULL = 120;

    const onTouchStart = (e) => {
      if (window.scrollY > 0) return;
      startY = e.touches[0].clientY;
      active = true;
    };
    const onTouchMove = (e) => {
      if (!active || startY === null) return;
      const delta = e.touches[0].clientY - startY;
      if (delta <= 0) {
        setPtrPull(0);
        return;
      }
      // Apply rubber-band damping so the pull never feels infinite.
      const damped = Math.min(MAX_PULL, delta * 0.5);
      setPtrPull(damped);
    };
    const onTouchEnd = async () => {
      if (!active) return;
      active = false;
      startY = null;
      if (ptrPull >= PULL_THRESHOLD && !ptrRefreshing) {
        setPtrRefreshing(true);
        try {
          if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
            try { navigator.vibrate(20); } catch (_) { /* noop */ }
          }
          await fetchAllExperiences();
        } finally {
          setPtrRefreshing(false);
          setPtrPull(0);
        }
      } else {
        setPtrPull(0);
      }
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ptrPull, ptrRefreshing]);

  // Canonical Explore ordering: most-clicked first, ties broken by original
  // backend index for stability. Re-derives whenever the catalog or click
  // stats change. The filter pipeline below operates on this ranked list,
  // so search/category filters preserve the popularity order. The selected
  // card on /experience/:slug is hoisted to position 0 separately by
  // `displayExperiences`, so its position is not disturbed by the sort.
  const rankedExperiences = useMemo(() => {
    if (!allExperiences || allExperiences.length === 0) return allExperiences;
    if (clickStats === null) return allExperiences;
    return allExperiences
      .map((exp, idx) => ({ exp, idx, clicks: clickStats[exp.id] || 0 }))
      .sort((a, b) => (b.clicks - a.clicks) || (a.idx - b.idx))
      .map((row) => row.exp);
  }, [allExperiences, clickStats]);

  // Filter experiences based on current filters
  useEffect(() => {
    let filtered = [...rankedExperiences];

    // Category filter (multi-select). The `events` slug is a virtual category
    // that filters on the `limited_event` boolean rather than an entry in
    // `exp.categories`.
    if (filters.categories && filters.categories.length > 0) {
      const isEventsOnly = filters.categories.length === 1 && filters.categories[0] === 'events';
      if (isEventsOnly) {
        filtered = filtered.filter(exp => !!exp.limited_event);
      } else {
        // Each UI slug matches BOTH the new canonical slug and the legacy
        // DB value (if any). This lets newly-created cards (which store the
        // new slug directly) and older legacy cards both satisfy the same
        // filter pill.
        const categorySlugAliases = {
          'tours_sightseeing': ['tours_sightseeing', 'tours'],
          'food_wine':         ['food_wine', 'gastronomy'],
          'water_activities':  ['water_activities', 'water_sports'],
          'culture_arts':      ['culture_arts', 'culture'],
          'beaches':           ['beaches'],
          'nightlife':         ['nightlife'],
          'romantic':          ['romantic'],
          'transfers':         ['transfers'],
        };

        const nonEventSlugs = filters.categories.filter(c => c !== 'events');
        const dbCategories = nonEventSlugs.flatMap(
          (slug) => categorySlugAliases[slug] || [slug]
        );
        const includesEvents = filters.categories.includes('events');

        filtered = filtered.filter(exp => {
          const matchesCat = exp.categories && exp.categories.some(cat => dbCategories.includes(cat));
          const matchesEvent = includesEvents && !!exp.limited_event;
          return matchesCat || matchesEvent;
        });
      }
    }

    // Location filter (multi-select)
    if (filters.locations && filters.locations.length > 0) {
      filtered = filtered.filter(exp => {
        const dest = (exp.destination || '').toLowerCase();
        return filters.locations.some(location => {
          if (location === 'sitges') return dest.includes('sitges');
          if (location === 'barcelona') return dest.includes('barcelona');
          if (location === 'tarragona') return dest.includes('tarragona');
          if (location === 'day_trips') return dest.includes('day_trip');
          return false;
        });
      });
    }

    // Duration filter (multi-select)
    if (filters.durations && filters.durations.length > 0) {
      filtered = filtered.filter(exp => {
        const duration = localized(exp, 'duration') || '';
        const durationLower = duration.toLowerCase();
        
        return filters.durations.some(dur => {
          if (dur === '0-3') {
            // Match short durations: 1-3 hours
            return durationLower.match(/\b[1-3]\s*(hour|hr)/i) || 
                   durationLower.includes('1 hour') || 
                   durationLower.includes('2 hour') || 
                   durationLower.includes('3 hour');
          }
          if (dur === '3-6') {
            // Match medium durations: 3-6 hours or half day
            return durationLower.match(/\b[3-6]\s*(hour|hr)/i) || 
                   durationLower.includes('half') ||
                   durationLower.includes('4 hour') || 
                   durationLower.includes('5 hour');
          }
          if (dur === '6+') {
            // Match long durations: 6+ hours, full day, or multiple days
            return durationLower.match(/\b([6-9]|[1-9][0-9]+)\s*(hour|hr)/i) ||
                   durationLower.includes('full') ||
                   durationLower.includes('day') ||
                   durationLower.includes('8 hour') ||
                   durationLower.includes('days');
          }
          return false;
        });
      });
    }

    // Language filter (multi-select) - check both live_guide and audio languages
    if (filters.languages && filters.languages.length > 0) {
      filtered = filtered.filter(exp => {
        const allLangs = [
          ...(exp.live_guide_languages || []),
          ...(exp.audio_languages || [])
        ];
        return filters.languages.some(filterLang => 
          allLangs.some(expLang => expLang.toLowerCase() === filterLang.toLowerCase())
        );
      });
    }

    // Search filter — match against the pre-computed lowercase haystack
    // (built in fetchAllExperiences). Falls back to per-card concat for
    // safety if the haystack wasn't built (e.g. tests / old cached data).
    if (filters.search && filters.search.trim()) {
      let searchTerm = filters.search.toLowerCase().trim();

      // If the search term matches a UI slug, also match against the legacy
      // DB alias (and vice versa). Keeps the search fast while surviving
      // cards seeded with either slug generation.
      const searchAliasMap = {
        'food_wine': ['food_wine', 'gastronomy'],
        'tours_sightseeing': ['tours_sightseeing', 'tours'],
        'water_activities': ['water_activities', 'water_sports'],
        'culture_arts': ['culture_arts', 'culture'],
      };
      const searchTerms = searchAliasMap[searchTerm] || [searchTerm];

      filtered = filtered.filter((exp) => {
        const haystack =
          exp._searchHaystack ||
          [
            exp.title_en,
            exp[`title_${lang}`],
            exp.description_en,
            exp[`description_${lang}`],
            exp.destination,
            (exp.categories || []).join(' '),
          ]
            .join(' ')
            .toLowerCase();
        return searchTerms.some((term) => haystack.includes(term));
      });
    }

    // Price filter
    const [minPrice, maxPrice] = filters.priceRange;
    filtered = filtered.filter(exp => exp.price >= minPrice && exp.price <= maxPrice);

    setExperiences(filtered);
  }, [filters, rankedExperiences]);

  // === /experience/:slug resolution ===
  // When a visitor arrives at /experience/:slug, look up the card, pick its
  // most-populated category, auto-apply that as a filter, and mark the card
  // as "selected" so the grid surfaces it first with a badge + halo.
  //
  // If the slug can't be resolved (deleted / deactivated experience) after
  // the catalog has loaded, bounce to /Explore per agreed edge-case rule.
  const selectedExp = useMemo(() => {
    if (!selectedSlug || allExperiences.length === 0) return null;
    return allExperiences.find((e) => e.slug === selectedSlug) || null;
  }, [selectedSlug, allExperiences]);

  // Determine the category with the most cards across the catalog. Ties
  // broken by the card's own `categories` author-order. Falls back to the
  // selected card's destination when it has no categories at all.
  const selectedPrimaryCategory = useMemo(() => {
    if (!selectedExp) return null;
    const cats = selectedExp.categories || [];
    if (cats.length === 0) return null;
    // Count catalog-wide occurrences of each of this card's categories.
    const counts = cats.map((slug) => {
      const n = allExperiences.reduce(
        (acc, e) => acc + ((e.categories || []).includes(slug) ? 1 : 0),
        0,
      );
      return { slug, count: n };
    });
    // Sort by count desc, preserving original author order on ties.
    counts.sort((a, b) => b.count - a.count);
    return counts[0]?.slug || null;
  }, [selectedExp, allExperiences]);

  // Apply the auto-picked category to the filter state once the selection
  // resolves, so the FilterBar reflects why the grid narrowed. Runs only
  // when the slug / chosen category actually changes.
  const lastAppliedSelection = useRef(null);
  const autoAppliedCategoryRef = useRef(null);
  useEffect(() => {
    if (!selectedSlug) return;
    const key = `${selectedSlug}::${selectedPrimaryCategory || ''}`;
    if (lastAppliedSelection.current === key) return;
    lastAppliedSelection.current = key;
    if (selectedPrimaryCategory) {
      autoAppliedCategoryRef.current = selectedPrimaryCategory;
      setFilters((prev) => ({ ...prev, categories: [selectedPrimaryCategory] }));
    } else if (selectedExp && selectedExp.destination) {
      // Fallback: no categories on the card — narrow by same destination.
      setFilters((prev) => ({ ...prev, locations: [selectedExp.destination] }));
    }
  }, [selectedSlug, selectedPrimaryCategory, selectedExp]);

  // When the user navigates away from /experience/:slug (e.g. by typing in
  // the header search bar, which bounces to /Explore?search=…), clear the
  // category we auto-applied so search results aren't accidentally narrowed
  // by it. We only clear it if the *current* category is still the one we
  // auto-applied — if the user has since picked a different category
  // manually, respect their choice.
  useEffect(() => {
    if (selectedSlug) return;
    const applied = autoAppliedCategoryRef.current;
    if (!applied) return;
    autoAppliedCategoryRef.current = null;
    lastAppliedSelection.current = null;
    setFilters((prev) => {
      if (prev.categories.length === 1 && prev.categories[0] === applied) {
        return { ...prev, categories: [] };
      }
      return prev;
    });
  }, [selectedSlug]);

  // 404 handling: slug not found after the catalog has loaded.
  useEffect(() => {
    if (!selectedSlug) return;
    if (allExperiences.length === 0) return;  // still loading
    if (!selectedExp) navigate('/Explore', { replace: true });
  }, [selectedSlug, selectedExp, allExperiences.length, navigate]);

  // Final grid ordering: when a selection is active, hoist it to position 0.
  const displayExperiences = useMemo(() => {
    if (!selectedExp) return experiences;
    const rest = experiences.filter((e) => e.id !== selectedExp.id);
    return [selectedExp, ...rest];
  }, [experiences, selectedExp]);

  const handleFilterChange = (newFilters) => {
    // Detect a meaningful navigation change (search, category, location).
    // When any of these change, scroll back to the top of the results so the
    // user immediately sees the new set instead of staying mid-list.
    const meaningfulChange =
      (newFilters.search || '') !== (filters.search || '') ||
      JSON.stringify(newFilters.categories || []) !== JSON.stringify(filters.categories || []) ||
      JSON.stringify(newFilters.locations || []) !== JSON.stringify(filters.locations || []);

    setFilters(newFilters);

    if (meaningfulChange) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }

    // Keep URL search params in sync with the search + category filters so
    // both search inputs (header desktop + filter-bar mobile) and the
    // Explore page stay consistent when one of them is changed. Other
    // filters (locations, durations, languages, price) remain in-memory.
    const params = new URLSearchParams(location.search);
    const newSearch = (newFilters.search || '').trim();
    const newCategory = (newFilters.categories && newFilters.categories[0]) || '';
    const oldSearch = params.get('search') || '';
    // Compare on the internal slug (URL stores the friendly alias for the
    // few mismatched categories) so a no-op like
    //   `?category=travel-essentials` ↔ filters.categories=['transfers']
    // doesn't trigger a redundant URL rewrite.
    const oldCategory = fromUrlCategorySlug(params.get('category') || '');
    const hasStaleFilterParam = params.has('filter');
    if (newSearch !== oldSearch || newCategory !== oldCategory || hasStaleFilterParam) {
      if (newSearch) params.set('search', newSearch);
      else params.delete('search');
      if (newCategory) params.set('category', toUrlCategorySlug(newCategory));
      else params.delete('category');
      // `?filter=events` is a one-shot homepage hint; strip it once the
      // user interacts with any FilterBar control so it can't bleed into
      // subsequent navigations.
      params.delete('filter');
      const qs = params.toString();
      navigate(qs ? `/Explore?${qs}` : '/Explore', { replace: true });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-[180px] lg:pt-[200px] pb-20" data-testid="explore-loading-skeleton">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
          <SkeletonGrid count={8} />
        </div>
      </div>
    );
  }

  // SEO title/description reflect the active category when the user deep-links
  // into /Explore?category=xxx — helps individual category pages rank for
  // their own keyword. When arriving at /experience/:slug, use the selected
  // experience's own metadata + JSON-LD TouristAttraction so each URL is a
  // unique Google-indexable page.
  const activeCat = filters.categories?.[0];
  const catLabel = activeCat ? (categories.find((c) => c.value === activeCat)?.label || activeCat) : '';

  let seoTitle;
  let seoDesc;
  const seoJsonLdBlocks = [];
  let seoImage = undefined;
  if (selectedExp) {
    const title = localized(selectedExp, 'title') || selectedExp.title_en;
    const desc = localized(selectedExp, 'description') || selectedExp.description_en || '';
    const destRaw = selectedExp.destination || '';
    const destLabel = t(`destination_${destRaw}`) !== `destination_${destRaw}`
      ? t(`destination_${destRaw}`)
      : destRaw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    seoTitle = destLabel
      ? `${title} — ${destLabel} | SitgesVibes`
      : `${title} | SitgesVibes`;
    seoDesc = (desc || title).slice(0, 158);
    seoImage = selectedExp.image;

    // JSON-LD TouristAttraction → unlocks Google rich results (stars, price).
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'TouristAttraction',
      name: title,
      description: desc,
      url: `https://sitgesvibes.com/experience/${selectedExp.slug}`,
      ...(selectedExp.image ? { image: selectedExp.image } : {}),
      ...(destLabel ? { touristType: destLabel } : {}),
    };
    if (Number(selectedExp.rating) > 0 && Number(selectedExp.review_count) > 0) {
      jsonLd.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: Number(selectedExp.rating),
        reviewCount: Number(selectedExp.review_count),
        bestRating: 5,
        worstRating: 1,
      };
    }
    if (Number(selectedExp.price) > 0) {
      jsonLd.offers = {
        '@type': 'Offer',
        price: Number(selectedExp.price),
        priceCurrency: selectedExp.currency || 'EUR',
        availability: 'https://schema.org/InStock',
        url: selectedExp.affiliate_url || `https://sitgesvibes.com/experience/${selectedExp.slug}`,
      };
    }
    seoJsonLdBlocks.push(jsonLd);

    // Invisible BreadcrumbList for /experience/:slug →
    //   Home › Explore › {Category} › {Title}
    // Category step is omitted when the card has no category.
    const breadcrumbs = [
      { name: 'Home', url: 'https://sitgesvibes.com/' },
      { name: 'Explore', url: 'https://sitgesvibes.com/Explore' },
    ];
    if (selectedPrimaryCategory) {
      const catName = categories.find((c) => c.value === selectedPrimaryCategory)?.label
        || selectedPrimaryCategory;
      breadcrumbs.push({
        name: catName,
        url: `https://sitgesvibes.com/Explore?category=${toUrlCategorySlug(selectedPrimaryCategory)}`,
      });
    }
    breadcrumbs.push({
      name: title,
      url: `https://sitgesvibes.com/experience/${selectedExp.slug}`,
    });
    seoJsonLdBlocks.push(buildBreadcrumbJsonLd(breadcrumbs));
  } else {
    seoTitle = catLabel
      ? `${catLabel} in Sitges, Barcelona & Beyond — SitgesVibes`
      : 'Explore All Experiences — SitgesVibes';
    seoDesc = catLabel
      ? `Browse ${catLabel.toLowerCase()} experiences in Sitges, Barcelona, Tarragona and beyond. Handpicked tours with local insider knowledge.`
      : 'Browse every tour, activity, food, nightlife and cultural experience across Sitges, Barcelona, Tarragona and nearby day trips.';

    // ItemList JSON-LD → tells Google this is a listing page and surfaces
    // the individual experiences as ranked list items (may appear in a
    // "Things to do" carousel). Falls back gracefully when the grid is
    // empty.
    seoJsonLdBlocks.push(
      buildItemListJsonLd(experiences, {
        name: catLabel ? `${catLabel} experiences` : 'Explore experiences',
        limit: 20,
      }),
    );

    // Invisible BreadcrumbList for /Explore →
    //   Home › Explore   (+ › {Category} if a category filter is active)
    const breadcrumbs = [
      { name: 'Home', url: 'https://sitgesvibes.com/' },
      { name: 'Explore', url: 'https://sitgesvibes.com/Explore' },
    ];
    if (catLabel) {
      breadcrumbs.push({
        name: catLabel,
        url: `https://sitgesvibes.com/Explore?category=${toUrlCategorySlug(activeCat)}`,
      });
    }
    seoJsonLdBlocks.push(buildBreadcrumbJsonLd(breadcrumbs));
  }

  const seoJsonLd = seoJsonLdBlocks.filter(Boolean);

  return (
    <>
      <SEOHead title={seoTitle} description={seoDesc} image={seoImage} jsonLd={seoJsonLd} />
      {/* Pull-to-refresh indicator (mobile only). Reveals as the user pulls
          down at the top of the page; once past the threshold it fires a
          refresh of the experiences list. */}
      {(ptrPull > 0 || ptrRefreshing) && (
        <div
          className="lg:hidden fixed top-0 left-0 w-full z-[60] flex items-end justify-center overflow-hidden pointer-events-none"
          style={{
            height: ptrRefreshing ? 56 : ptrPull,
            transition: ptrRefreshing ? 'height 200ms ease-out' : 'none',
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,0.6))',
          }}
          data-testid="ptr-indicator"
        >
          <div className="ptr-indicator pb-3">
            {ptrRefreshing ? (
              <>
                <span className="ptr-spinner" />
                Refreshing…
              </>
            ) : ptrPull >= 80 ? (
              <>↑ Release to refresh</>
            ) : (
              <>↓ Pull to refresh</>
            )}
          </div>
        </div>
      )}

      {/* Sticky FilterBar — Option A: always `position: fixed` on mobile +
          desktop on smart-sticky pages so it never has to swap layout
          modes. Slides in lockstep with the Header via a CSS transform
          driven by `html[data-pin-state]`. See .filter-bar-sticky in
          App.css. */}
      <div className="filter-bar-sticky fixed top-[83px] lg:top-[75px] left-0 w-full z-40 transition-transform duration-300 ease-out"
           style={{
             background: '#ffffff',
             boxShadow: scrolled ? '0 4px 18px rgba(15,59,99,0.10)' : 'none',
             borderBottom: scrolled ? 'none' : '1px solid rgba(0,0,0,0.1)'
           }}>
        <div className="max-w-[1280px] mx-auto px-0 lg:px-8 py-4">
          <FilterBar 
            filters={filters}
            onFilterChange={handleFilterChange}
            categories={categories}
            availableLanguages={availableLanguages}
            clearAllSignal={clearAllSignal}
          />
        </div>
      </div>

      {/* Header + FilterBar spacer — both elements are permanently fixed
          on smart-sticky pages, so the grid below needs to skip past
          their combined heights. Mobile is taller because the filter
          bar embeds a search input on small viewports. */}
      <div className="h-[220px] lg:h-[160px]" />

      {/* Only Experience Cards */}
      <section className="pt-[38px] pb-12 lg:pt-[18px] sm:pb-16">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8">

          {experiences.length === 0 ? (
            (() => {
              const searchTerm = (filters.search || '').trim();
              const activeFilterCount =
                (filters.categories?.length || 0) +
                (filters.locations?.length || 0) +
                (filters.durations?.length || 0) +
                (filters.languages?.length || 0) +
                (searchTerm ? 1 : 0);
              return (
                <div
                  className="text-center py-20 max-w-md mx-auto"
                  data-testid="explore-empty-state"
                >
                  <h3 className="text-[22px] sm:text-[24px] font-bold mb-2" style={{ color: '#1a4d7a' }}>
                    {searchTerm ? t('empty_no_match_title') : t('empty_no_results_title')}
                  </h3>
                  <p className="text-base mb-1" style={{ color: '#2d5f8d' }}>
                    {searchTerm ? (
                      <>
                        {t('empty_no_match_for')} <span className="font-semibold text-[#1a4d7a]">“{searchTerm}”</span>.
                      </>
                    ) : (
                      t('empty_try_broader')
                    )}
                  </p>
                  {activeFilterCount > 1 && (
                    <p className="text-sm mb-5" style={{ color: '#6b7a8c' }}>
                      {t('empty_remove_filter_hint')}
                    </p>
                  )}
                  <div className="mt-5 flex items-center justify-center">
                    <button
                      onClick={() => {
                        setFilters({
                          categories: [],
                          locations: [],
                          durations: [],
                          languages: [],
                          priceRange: [0, maxPrice],
                          search: ''
                        });
                        setClearAllSignal((c) => c + 1);
                        // Also wipe the URL so the desktop header search bar
                        // (which mirrors `?search=`) clears too.
                        navigate('/Explore', { replace: true });
                        // Explicit scroll — pathname stays `/Explore`, so
                        // neither ScrollToTop nor the searchParams effect
                        // fires when the previous state was already clean
                        // of URL-tracked filters. rAF runs the scroll AFTER
                        // React re-renders the now-unfiltered grid, so a
                        // layout change can't snap the page back to the
                        // wishlisted-card position we came from.
                        requestAnimationFrame(() =>
                          window.scrollTo({ top: 0, behavior: 'instant' })
                        );
                      }}
                      className="h-11 px-5 rounded-full bg-[#3c84ff] hover:bg-[#2a6fe0] text-white text-sm font-semibold transition-colors active:scale-95"
                      data-testid="empty-clear-all"
                    >
                      {t('clear_all_filters')}
                    </button>
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {displayExperiences.map((exp, i) => (
                <ExperienceCard
                  key={exp.id}
                  exp={exp}
                  selected={selectedExp && selectedExp.id === exp.id}
                  testIdPrefix="experiences-page"
                  containerClassName="h-[447px] sm:h-[420px]"
                  animationDelay={i * 0.05}
                  resetSignal={filtersResetSignal}
                />
              ))}
              {(() => {
                const hasActiveFilters =
                  (filters.categories || []).length > 0 ||
                  (filters.locations || []).length > 0 ||
                  (filters.durations || []).length > 0 ||
                  (filters.languages || []).length > 0 ||
                  (filters.search || '').trim() !== '' ||
                  filters.priceRange[0] !== 0 ||
                  filters.priceRange[1] !== maxPrice;
                if (!hasActiveFilters) return null;
                return (
                  <div
                    className="h-[420px] rounded-2xl flex flex-col items-center justify-center gap-4"
                    data-testid="clear-all-filters-card"
                  >
                    <button
                      onClick={() => {
                        setFilters({
                          categories: [],
                          locations: [],
                          durations: [],
                          languages: [],
                          priceRange: [0, maxPrice],
                          search: ''
                        });
                        setClearAllSignal((c) => c + 1);
                        navigate('/Explore', { replace: true });
                        // Same reasoning as the empty-state clear-all above.
                        requestAnimationFrame(() =>
                          window.scrollTo({ top: 0, behavior: 'instant' })
                        );
                      }}
                      data-testid="clear-all-filters-card-button"
                      className="w-20 h-20 rounded-full bg-[#3c84ff] hover:bg-[#2a6fe0] flex items-center justify-center transition-all duration-300 hover:scale-110"
                      aria-label={t('clear_all_filters')}
                    >
                      <FilterX className="w-10 h-10 text-white" strokeWidth={2.5} />
                    </button>
                    <span
                      className="text-[#0F3B63] font-bold text-lg"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {t('clear_all_filters')}
                    </span>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
}
